// packages/agent-kit/src/conversations/useConversation/sseHandlers.ts

import type {
  UIMessage,
  SSEMessageEventData,
  SSEResponseChunkEvent,
  SSECompleteEvent,
  SSEProgressUpdateEvent,
  Message,
} from '@dopomogai/types';
import { AgentKitError } from '../../types';

const parseMaybeJson = (raw: unknown): string | Record<string, any> => {
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        return parsed as Record<string, any>;
      }
    } catch {
      // falls through to returning the original string
    }
    return raw;
  }

  if (raw && typeof raw === 'object') {
    return raw as Record<string, any>;
  }

  return String(raw ?? '');
};

export const processBackendMessagesToUI = (messages: Message[]): UIMessage[] => {
  const processed: UIMessage[] = [];
  if (!Array.isArray(messages)) return [];

  for (const [index, message] of messages.entries()) {
    const baseKey = message.id || `${message.timestamp}-${index}`;

    if (message.kind === 'request' && message.parts) {
      const userPromptPart = message.parts.find(p => p.part_kind === 'user-prompt');
      if (userPromptPart) {
        processed.push({
          id: `hist-${baseKey}-user`,
          type: 'user',
          content: userPromptPart.content as string,
          timestamp: message.timestamp,
        });
      }
    } else if (message.kind === 'response' && message.parts) {
      for (const [partIndex, part] of (message.parts || []).entries()) {
        const partKey = `${baseKey}-${part.part_kind}-${partIndex}`;

        if (part.part_kind === 'llm-response' || part.part_kind === 'text') {
          processed.push({
            id: partKey,
            type: 'assistant',
            content: part.content as string,
            model: message.model_name || undefined,
            timestamp: message.timestamp,
            isStreaming: false,
          });
        } else if (part.part_kind === 'tool-call') {
          const toolCallId = part.tool_call_id || `tool-call-${partKey}`;
          let parsedArgs: Record<string, any> = {};
          try {
            if (part.args && typeof part.args === 'string') {
              parsedArgs = JSON.parse(part.args);
            } else if (part.args && typeof part.args === 'object') {
              parsedArgs = part.args;
            }
          } catch {
            parsedArgs = { _error: 'Failed to parse arguments', _raw: part.args };
          }

          processed.push({
            id: toolCallId,
            type: 'tool_call_start',
            tool_call_id: toolCallId,
            tool_name: part.tool_name!,
            arguments: parsedArgs,
            timestamp: message.timestamp,
          });
        } else if (
          part.part_kind === 'tool-response' ||
          part.part_kind === 'tool-result' ||
          part.part_kind === 'tool-return'
        ) {
          const toolCallId = part.tool_call_id || `tool-call-result-${partKey}`;
          const output = (part as any).output ?? part.content ?? 'Tool executed.';
          processed.push({
            id: `resp-for-${toolCallId}`,
            type: 'tool_response_end',
            tool_call_id: toolCallId,
            tool_name: part.tool_name!,
            output: parseMaybeJson(output),
            timestamp: message.timestamp,
          });
        } else if (part.part_kind === 'context-info') {
          // Normalize context-info into a 'system_log' message (info severity)
          const raw = (part as any).content;
          const contentStr =
            typeof raw === 'string' ? raw : JSON.stringify(raw, null, 2);
          processed.push({
            id: partKey,
            type: 'system_log',
            content: contentStr,
            severity: 'info',
            timestamp: message.timestamp,
          } as any);
        } else if (part.part_kind === 'error') {
          const raw = (part as any).content || (part as any).message || (part as any);
          const contentStr =
            typeof raw === 'string' ? raw : JSON.stringify(raw, null, 2);
          processed.push({
            id: partKey,
            type: 'system_log',
            content: contentStr,
            severity: 'error',
            timestamp: message.timestamp,
          } as any);
        }
      }
    }
  }
  return processed;
};

export function handleProgressUpdate(
  event: SSEProgressUpdateEvent,
  setMessages: React.Dispatch<React.SetStateAction<UIMessage[]>>
) {
  const progressEvent = event;
  if (progressEvent.update_type === 'tool_call') {
    const toolContent = progressEvent.content;
    let parsedArgs: Record<string, any> = {};
    try {
      if (toolContent.args && typeof toolContent.args === 'string') {
        parsedArgs = JSON.parse(toolContent.args);
      } else if (toolContent.args && typeof toolContent.args === 'object') {
        parsedArgs = toolContent.args;
      }
    } catch {
      parsedArgs = { _error: 'Failed to parse arguments', _raw: toolContent.args };
    }
    setMessages(prev => [
      ...prev,
      {
        id: toolContent.tool_call_id,
        type: 'tool_call_start',
        tool_call_id: toolContent.tool_call_id,
        tool_name: toolContent.tool_name,
        arguments: parsedArgs,
        timestamp: progressEvent.timestamp,
        // Mark as live so FE can auto-run headless only for streaming-origin events
        ...( { meta: { live: true } } as any ),
      },
    ]);
  } else if (progressEvent.update_type === 'tool_result') {
    const toolContent = progressEvent.content;
    const id = `resp-for-${toolContent.tool_call_id}`;
    setMessages(prev => {
      const exists = prev.some(m => m.id === id);
      if (exists) return prev;
      return [
        ...prev,
        {
          id,
          type: 'tool_response_end',
          tool_call_id: toolContent.tool_call_id,
          tool_name: toolContent.tool_name,
          output: toolContent.result_preview ?? 'Tool executed.',
          timestamp: progressEvent.timestamp,
        },
      ];
    });
  }
}

export function handleTopLevelToolEvents(
  _parsedData: SSEMessageEventData,
  _setMessages: React.Dispatch<React.SetStateAction<UIMessage[]>>
) {
  // No-op: backend emits tool events via progress_update; top-level tool events not used.
}

export function handleResponseChunk(
  chunkEvent: SSEResponseChunkEvent,
  assistantMessageIdRef: React.MutableRefObject<string | null>,
  streamingContentRef: React.MutableRefObject<string>,
  setMessages: React.Dispatch<React.SetStateAction<UIMessage[]>>
) {
  if (!assistantMessageIdRef.current) {
    const newAssistantId = `assistant-${Date.now()}`;
    assistantMessageIdRef.current = newAssistantId;
    streamingContentRef.current = chunkEvent.content || '';
    setMessages(prev => [
      ...prev,
      {
        id: newAssistantId,
        type: 'assistant',
        content: streamingContentRef.current,
        isStreaming: true,
        timestamp: chunkEvent.timestamp,
      },
    ]);
  } else {
    streamingContentRef.current += chunkEvent.content || '';
    const currentId = assistantMessageIdRef.current;
    setMessages(prev =>
      prev.map(m => (m.id === currentId ? { ...m, content: streamingContentRef.current, isStreaming: true } : m))
    );
  }
}

export function handleComplete(
  completeEvent: SSECompleteEvent,
  assistantMessageIdRef: React.MutableRefObject<string | null>,
  streamingContentRef: React.MutableRefObject<string>,
  setMessages: React.Dispatch<React.SetStateAction<UIMessage[]>>,
  setUsage: React.Dispatch<React.SetStateAction<{ cost: number; tokensInput: number; tokensOutput: number }>>,
  optimisticUserId?: string
) {
  setUsage(prev => ({
    cost: prev.cost + (completeEvent.cost ?? 0),
    tokensInput: prev.tokensInput + (completeEvent.tokens_input ?? 0),
    tokensOutput: prev.tokensOutput + (completeEvent.tokens_output ?? 0),
  }));

  const finalText =
    (typeof completeEvent.final_response === 'string' && completeEvent.final_response.length > 0)
      ? completeEvent.final_response
      : streamingContentRef.current;

  const currentId = assistantMessageIdRef.current;
  if (currentId) {
    setMessages(prev =>
      prev.map(msg => (msg.id === currentId ? { ...msg, content: finalText, isStreaming: false } : msg))
    );
  }
  if (optimisticUserId) {
    setMessages(prev =>
      prev.map(m => (m.id === optimisticUserId ? { ...m, status: undefined, retryPayload: undefined } : m))
    );
  }
  assistantMessageIdRef.current = null;
  streamingContentRef.current = '';
}

export function handleStreamEnd(
  assistantMessageIdRef: React.MutableRefObject<string | null>,
  streamingContentRef: React.MutableRefObject<string>,
  setMessages: React.Dispatch<React.SetStateAction<UIMessage[]>>
) {
  const currentId = assistantMessageIdRef.current;
  if (currentId) {
    setMessages(prev =>
      prev.map(m => (m.id === currentId ? { ...m, isStreaming: false } : m))
    );
  }
  assistantMessageIdRef.current = null;
  streamingContentRef.current = '';
}

export function handleError(
  parsedData: any,
  setError: (e: AgentKitError) => void,
  setMessages: React.Dispatch<React.SetStateAction<UIMessage[]>>,
  optimisticUserId?: string,
  assistantMessageIdRef?: React.MutableRefObject<string | null>,
  streamingContentRef?: React.MutableRefObject<string>
) {
  const msg = parsedData?.message || parsedData?.error || 'Stream error';
  const details = parsedData?.details || parsedData?.error_details;
  setError(new AgentKitError(msg, details));

  if (optimisticUserId) {
    setMessages(prev => prev.map(m => (m.id === optimisticUserId ? { ...m, status: 'error' } : m)));
  }
  if (assistantMessageIdRef) assistantMessageIdRef.current = null;
  if (streamingContentRef) streamingContentRef.current = '';
}
